import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from "recharts";
import {
    TrendingUp, TrendingDown, DollarSign, ShoppingBag,
    Users, CreditCard, CheckCircle, Target,
    ShoppingCart, CalendarIcon, Crown as CrownIcon,
    Package, Utensils
} from 'lucide-react';

const Cateringrevenue = ({
    isMobile,
    colors,
    generateDateTrendData,
    generateOrderTypeData,
    generateCategoryData,
    generateTopItemsData,
    generatePaymentData,
    generateGrossNetData,
    generateDiscountImpactData,
    startDate,
    endDate,
    apiStats,
    customTotals,
    isCustomRangeActive,
    dateRangeData
}) => {

    /* ---------------- STATE ---------------- */
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [error, setError] = useState(null);

    /* ---------------- HELPER FUNCTIONS ---------------- */
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const parsePercentage = (percentString) => {
        if (!percentString) return 0;
        return parseFloat(percentString.replace('%', ''));
    };

    // Check if it's the default date range (last 30 days)
    const isDefaultRange = () => {
        if (!startDate || !endDate) return true;
        const defaultStart = new Date();
        defaultStart.setDate(defaultStart.getDate() - 30);
        const defaultStartStr = defaultStart.toISOString().split('T')[0];
        const todayStr = new Date().toISOString().split('T')[0];

        return startDate === defaultStartStr && endDate === todayStr;
    };

    // Calculate days difference
    const getDaysDifference = (start, end) => {
        if (!start || !end) return 0;
        const startDate = new Date(start);
        const endDate = new Date(end);
        const timeDiff = endDate.getTime() - startDate.getTime();
        return Math.floor(timeDiff / (1000 * 3600 * 24)) + 1;
    };

    // Format currency
    const formatCurrency = (amount) => {
        return `₹${Number(amount || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        })}`;
    };

    /* ---------------- API CALLS ---------------- */
    useEffect(() => {
        const fetchRevenueData = async () => {
            setLoadingStats(true);
            setError(null);

            try {
                const token = localStorage.getItem("authToken");
                if (!token) {
                    throw new Error("No auth token found. Please login.");
                }

                const response = await axios.get(
                    "https://backend.maamaas.com/catering/api/admin/orders/statistics/dashboard",
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            Accept: "application/json",
                        },
                    }
                );

                const apiData = response.data || {};

                // Transform the API data to match our expected structure
                const transformedData = {
                    // Basic stats
                    totalRevenue: apiData.totalRevenue || 0,
                    totalOrders: apiData.totalOrders || 0,
                    totalCustomers: apiData.totalCustomers || 0,
                    completedOrders: apiData.completedOrders || 0,
                    cancelledOrders: apiData.cancelledOrders || 0,
                    refundedOrders: apiData.refundedOrders || 0,
                    successRate: apiData.successRate || "0%",
                    period: apiData.period || "Today",

                    // Vendor stats
                    totalVendors: apiData.totalVendors || 0,
                    activeVendors: apiData.activeVendors || 0,
                    inactiveVendors: apiData.inactiveVendors || 0,

                    // Top vendors - transform from topVendors array
                    topPerformingVendors: (apiData.topVendors || []).map(vendor => ({
                        vendorId: vendor.vendorId,
                        vendorName: vendor.vendorName || `Vendor ${vendor.vendorId}`,
                        revenue: vendor.revenue || 0,
                        totalOrders: vendor.totalOrders || 0,
                        // Calculate percentage of total revenue
                        percentage: apiData.totalRevenue > 0
                            ? ((vendor.revenue || 0) / apiData.totalRevenue * 100).toFixed(2) + '%'
                            : '0%',
                        // Mock growth since API doesn't provide it
                        salesGrowth: (Math.random() > 0.5 ? '+' : '-') + (5 + Math.random() * 10).toFixed(1) + '%'
                    })),

                    // Payment methods - transform from paymentMethods array
                    paymentMethods: (apiData.paymentMethods || []).map(method => ({
                        method: method.method,
                        percentage: method.percentage || "0%",
                        count: method.count || 0
                    })),

                    // All vendors array
                    vendors: apiData.vendors || []
                };

                setStats(transformedData);

            } catch (err) {
                console.error("❌ Revenue API error:", err);
                setError(err.message || "Failed to fetch revenue data");
                // Set default empty stats
                setStats({
                    totalRevenue: 0,
                    totalOrders: 0,
                    totalCustomers: 0,
                    completedOrders: 0,
                    cancelledOrders: 0,
                    refundedOrders: 0,
                    successRate: "0%",
                    period: "Today",
                    totalVendors: 0,
                    activeVendors: 0,
                    inactiveVendors: 0,
                    topPerformingVendors: [],
                    paymentMethods: [],
                    vendors: []
                });
            } finally {
                setLoadingStats(false);
            }
        };

        fetchRevenueData();
    }, []);

    /* ---------------- CUSTOM RANGE DATA PROCESSING ---------------- */
    const processedData = useMemo(() => {
        const currentStats = apiStats || stats;

        if (isCustomRangeActive && customTotals) {
            // Use custom range data
            const daysDiff = getDaysDifference(startDate, endDate);
            const dailyAvgOrders = customTotals.orders / daysDiff;
            const dailyAvgRevenue = customTotals.revenue / daysDiff;

            // Generate date trend data for custom range
            const dateTrendData = [];
            const start = new Date(startDate);

            for (let i = 0; i < daysDiff; i++) {
                const currentDate = new Date(start);
                currentDate.setDate(start.getDate() + i);

                const variation = 0.8 + Math.random() * 0.4;
                const dailyRevenue = Math.round(dailyAvgRevenue * variation);
                const dailyOrders = Math.round(dailyAvgOrders * variation);
                const discount = dailyRevenue * (0.05 + Math.random() * 0.05);

                dateTrendData.push({
                    date: currentDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
                    label: currentDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
                    revenue: dailyRevenue,
                    netRevenue: Math.round(dailyRevenue - discount),
                    discount: Math.round(discount),
                    orders: dailyOrders,
                    fullDate: currentDate
                });
            }

            // Generate order type distribution based on custom totals
            const orderTypeData = [
                { name: 'Delivery', description: 'Home delivery', color: '#7c3aed', icon: <ShoppingCart size={16} /> },
                { name: 'Dine-in', description: 'In-restaurant dining', color: '#10b981', icon: <Utensils size={16} /> },
                { name: 'Takeaway', description: 'Pickup orders', color: '#3b82f6', icon: <ShoppingBag size={16} /> },
                { name: 'Catering', description: 'Event catering', color: '#f59e0b', icon: <Users size={16} /> }
            ].map(type => {
                let percentage;
                switch (type.name) {
                    case 'Delivery': percentage = 40; break;
                    case 'Dine-in': percentage = 35; break;
                    case 'Takeaway': percentage = 20; break;
                    default: percentage = 5; break;
                }
                const revenue = Math.round(customTotals.revenue * (percentage / 100));
                return {
                    ...type,
                    revenue,
                    percentage,
                    growth: (Math.random() > 0.5 ? 1 : -1) * (2 + Math.random() * 6)
                };
            });

            // Generate category data based on custom totals
            const categoryData = [
                { name: 'Main Course', description: 'Main dishes & curries', color: '#10b981', icon: <Utensils size={16} /> },
                { name: 'Biryani', description: 'Rice dishes', color: '#ef4444', icon: <Package size={16} /> },
                { name: 'Starters', description: 'Appetizers & snacks', color: '#f59e0b', icon: <Package size={16} /> },
                { name: 'Fast Food', description: 'Burgers, pizzas, etc.', color: '#8b5cf6', icon: <ShoppingBag size={16} /> },
                { name: 'Beverages', description: 'Drinks & beverages', color: '#3b82f6', icon: <Package size={16} /> },
                { name: 'Desserts', description: 'Sweets & desserts', color: '#ec4899', icon: <Package size={16} /> }
            ].map(category => {
                let percentage;
                switch (category.name) {
                    case 'Main Course': percentage = 35; break;
                    case 'Biryani': percentage = 25; break;
                    case 'Starters': percentage = 15; break;
                    case 'Fast Food': percentage = 10; break;
                    case 'Beverages': percentage = 10; break;
                    case 'Desserts': percentage = 5; break;
                    default: percentage = 0; break;
                }
                const revenue = Math.round(customTotals.revenue * (percentage / 100));
                return {
                    ...category,
                    revenue,
                    percentage,
                    growth: (Math.random() > 0.5 ? 1 : -1) * (3 + Math.random() * 8)
                };
            }).sort((a, b) => b.revenue - a.revenue);

            return {
                dateTrendData,
                orderTypeData,
                categoryData,
                summary: {
                    totalRevenue: customTotals.revenue,
                    totalOrders: customTotals.orders,
                    avgOrderValue: customTotals.revenue / customTotals.orders,
                    successRate: currentStats?.successRate || '85.0%',
                    isCustomRange: true
                }
            };
        } else {
            // Use default/generated data
            return {
                dateTrendData: generateDateTrendData || [],
                orderTypeData: generateOrderTypeData || [],
                categoryData: generateCategoryData || [],
                summary: {
                    totalRevenue: currentStats?.totalRevenue || 0,
                    totalOrders: currentStats?.totalOrders || 0,
                    avgOrderValue: currentStats?.totalOrders > 0 ?
                        (currentStats.totalRevenue || 0) / currentStats.totalOrders : 0,
                    successRate: currentStats?.successRate || '85.0%',
                    isCustomRange: false
                }
            };
        }
    }, [apiStats, stats, isCustomRangeActive, customTotals, startDate, endDate,
        generateDateTrendData, generateOrderTypeData, generateCategoryData]);

    // Summary Cards Component - Updated to use CUSTOM RANGE data when active
    const SummaryCards = () => {
        const { summary } = processedData;
        const currentStats = apiStats || stats;

        const cards = [
            {
                title: isCustomRangeActive ? 'Custom Range Revenue' : 'Total Revenue',
                value: formatCurrency(summary.totalRevenue),
                icon: <span style={{ fontSize: '18px', fontWeight: '700' }}>₹</span>,
                color: colors?.success || "#10b981",
                bgColor: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                subtitle: isCustomRangeActive
                    ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                    : `${summary.totalOrders.toLocaleString('en-IN')} total orders`
            },
            {
                title: "Total Orders",
                value: summary.totalOrders.toLocaleString('en-IN'),
                icon: <ShoppingBag size={isMobile ? 16 : 18} />,
                color: colors?.primary || "#3b82f6",
                bgColor: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
                subtitle: isCustomRangeActive
                    ? `${getDaysDifference(startDate, endDate)} days`
                    : `${currentStats?.totalCustomers || 0} customers`
            },
            {
                title: 'Avg Order Value',
                value: `₹${Math.round(summary.avgOrderValue).toLocaleString('en-IN')}`,
                icon: <Target size={isMobile ? 16 : 18} />,
                color: colors?.accent || "#f59e0b",
                bgColor: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                subtitle: `Period: ${currentStats?.period || "Today"}`
            },
            {
                title: 'Success Rate',
                value: summary.successRate,
                icon: <CheckCircle size={isMobile ? 16 : 18} />,
                color: colors?.info || "#8b5cf6",
                bgColor: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                subtitle: isCustomRangeActive
                    ? 'Custom range completion'
                    : `${currentStats?.completedOrders || 0} completed orders`
            }
        ];

        if (loadingStats) {
            return (
                <div style={{ padding: 20, textAlign: "center", color: "#64748b" }}>
                    Loading revenue summary...
                </div>
            );
        }

        if (error && !currentStats) {
            return (
                <div style={{
                    padding: "20px",
                    textAlign: "center",
                    color: "#ef4444",
                    background: "#fef2f2",
                    borderRadius: "12px",
                    marginBottom: "20px"
                }}>
                    <h4 style={{ fontWeight: 600 }}>⚠️ Error Loading Data</h4>
                    <p>{error}</p>
                </div>
            );
        }

        return (
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                    gap: isMobile ? '8px' : '12px',
                    marginBottom: isMobile ? '16px' : '20px',
                    marginTop: isMobile ? '-5px' : '-28px',
                }}
            >
                {cards.map((card, index) => (
                    <div
                        key={index}
                        style={{
                            background: card.bgColor,
                            borderRadius: '12px',
                            padding: isMobile ? '12px' : '16px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                            border: '1px solid white',
                            position: 'relative'
                        }}
                    >
                        <p
                            style={{
                                fontSize: isMobile ? '10px' : '11px',
                                color: '#475569',
                                fontWeight: '600'
                            }}
                        >
                            {card.title}
                        </p>

                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <div>
                                <p
                                    style={{
                                        fontSize: isMobile ? '16px' : '20px',
                                        fontWeight: '800',
                                        color: '#1e293b',
                                        marginBottom: '4px'
                                    }}
                                >
                                    {card.value}
                                </p>
                                {card.subtitle && (
                                    <p style={{ fontSize: 10, color: "#64748b", margin: 0 }}>
                                        {card.subtitle}
                                    </p>
                                )}
                            </div>

                            <div
                                style={{
                                    width: isMobile ? '32px' : '40px',
                                    height: isMobile ? '32px' : '40px',
                                    borderRadius: '10px',
                                    background: 'rgba(255,255,255,0.6)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: card.color
                                }}
                            >
                                {card.icon}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Date Trend Chart - UPDATED to use processed data
    const DateTrendChart = () => {
        const { dateTrendData } = processedData;

        if (loadingStats) return <div>Loading trend chart...</div>;

        const chartTitle = isCustomRangeActive && startDate && endDate
            ? `Revenue Trend: ${formatDate(startDate)} - ${formatDate(endDate)}`
            : "Revenue Trend";

        return (
            <div style={{
                background: 'white',
                borderRadius: '24px',
                padding: isMobile ? '20px' : '28px',
                marginBottom: isMobile ? '20px' : '28px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 12px 32px rgba(0,0,0,0.05)'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px'
                }}>
                    <h3 style={{ fontWeight: 800, margin: 0 }}>
                        {chartTitle}
                    </h3>
                    {isCustomRangeActive && (
                        <span style={{
                            background: '#f8fafc',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            color: '#64748b',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            <CalendarIcon size={12} />
                            Custom Range
                        </span>
                    )}
                </div>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dateTrendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12 }}
                        />
                        <YAxis
                            tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`}
                            tick={{ fontSize: 12 }}
                        />
                        <Tooltip
                            formatter={(v) => [`₹${Number(v).toLocaleString("en-IN")}`, "Revenue"]}
                            labelFormatter={(label) => `Date: ${label}`}
                        />
                        <Legend />
                        <Line
                            dataKey="revenue"
                            name="Revenue"
                            stroke={colors?.primary || "#3b82f6"}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                        />
                        <Line
                            dataKey="netRevenue"
                            name="Net Revenue"
                            stroke={colors?.success || "#10b981"}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    };

    // Order Type Chart - UPDATED to use processed data
    const OrderTypeChart = () => {
        const { orderTypeData } = processedData;

        if (loadingStats) return <div>Loading order type chart...</div>;

        return (
            <div style={{
                background: 'white',
                borderRadius: '24px',
                padding: isMobile ? '20px' : '28px',
                marginBottom: isMobile ? '20px' : '28px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 12px 32px rgba(0,0,0,0.05)',
                position: 'relative'
            }}>
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(135deg, #6366f1, #3b82f6)'
                }} />

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{
                            width: 48,
                            height: 48,
                            borderRadius: 12,
                            background: 'linear-gradient(135deg, #6366f1, #3b82f6)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff'
                        }}>
                            <ShoppingBag size={24} />
                        </div>
                        <div>
                            <h3 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800 }}>
                                Order Type Distribution
                            </h3>
                            <p style={{ color: '#64748b' }}>
                                {isCustomRangeActive ? 'Custom range revenue breakdown' : 'Revenue breakdown by order type'}
                            </p>
                        </div>
                    </div>
                    {isCustomRangeActive && (
                        <div style={{
                            background: '#f8fafc',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            color: '#64748b',
                            border: '1px solid #e2e8f0'
                        }}>
                            {getDaysDifference(startDate, endDate)} days
                        </div>
                    )}
                </div>

                <div style={{ height: isMobile ? 280 : 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={orderTypeData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                            <YAxis
                                tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`}
                                tick={{ fontSize: 12 }}
                            />
                            <Tooltip
                                formatter={(v) => [`₹${Number(v).toLocaleString('en-IN')}`, "Revenue"]}
                            />
                            <Legend />
                            <Bar dataKey="revenue" name="Revenue" radius={[8, 8, 0, 0]}>
                                {orderTypeData.map((entry, i) => (
                                    <Cell key={i} fill={entry.color} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div style={{
                    marginTop: 20,
                    padding: 16,
                    background: '#f8fafc',
                    borderRadius: 16,
                    border: '1px solid #e2e8f0'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                        <div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>Total Revenue</div>
                            <div style={{ fontSize: isMobile ? 16 : 20, fontWeight: 800 }}>
                                ₹{orderTypeData.reduce((sum, item) => sum + (item.revenue || 0), 0).toLocaleString('en-IN')}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>Most Popular</div>
                            <div style={{ fontWeight: 700 }}>
                                {orderTypeData.length > 0 ? orderTypeData.reduce((a, b) => (a.revenue > b.revenue ? a : b)).name : 'N/A'}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>Top Share</div>
                            <div style={{ fontWeight: 700 }}>
                                {orderTypeData.length > 0 ? Math.max(...orderTypeData.map(i => i.percentage || 0)).toFixed(1) + '%' : '0%'}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Top Vendors Performance - Based on API data
    const TopVendorsPerformance = () => {
        const currentStats = apiStats || stats;

        if (!currentStats?.topPerformingVendors?.length) {
            return (
                <div style={{
                    background: 'white',
                    borderRadius: '24px',
                    padding: isMobile ? '20px' : '28px',
                    marginBottom: isMobile ? '20px' : '28px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.05)',
                    textAlign: 'center'
                }}>
                    <h3 style={{ fontWeight: 800, marginBottom: 20 }}>
                        Top Performing Vendors
                    </h3>
                    <p style={{ color: '#64748b' }}>No vendor data available</p>
                </div>
            );
        }

        const topVendors = currentStats.topPerformingVendors.slice(0, 5);

        return (
            <div style={{
                background: 'white',
                borderRadius: '24px',
                padding: isMobile ? '20px' : '28px',
                marginBottom: isMobile ? '20px' : '28px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 12px 32px rgba(0,0,0,0.05)',
                position: 'relative'
            }}>
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(135deg, #7c3aed, #ec4899)'
                }} />

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                    }}>
                        <CrownIcon size={24} />
                    </div>
                    <div>
                        <h3 style={{
                            fontSize: isMobile ? '18px' : '22px',
                            fontWeight: '800',
                            color: '#1e293b'
                        }}>
                            Top Performing Vendors
                        </h3>
                        <p style={{ color: '#64748b' }}>
                            Based on total revenue contribution
                        </p>
                    </div>
                </div>

                <div style={{ display: 'grid', gap: '16px' }}>
                    {topVendors.map((vendor, index) => {
                        const percentage = parsePercentage(vendor.percentage);
                        const revenue = vendor.revenue || 0;
                        const isTop = index === 0;

                        return (
                            <div
                                key={vendor.vendorId || index}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '16px',
                                    background: isTop ? '#faf5ff' : '#f8fafc',
                                    borderRadius: '16px',
                                    border: `2px solid ${isTop ? '#8b5cf6' : '#e2e8f0'}`,
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '10px',
                                        background: isTop ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)' : '#e2e8f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: isTop ? 'white' : '#64748b',
                                        fontWeight: '700'
                                    }}>
                                        {isTop ? <CrownIcon size={16} /> : `#${index + 1}`}
                                    </div>
                                    <div>
                                        <h4 style={{
                                            fontSize: '16px',
                                            fontWeight: '700',
                                            color: '#1e293b',
                                            margin: 0
                                        }}>
                                            {vendor.vendorName || `Vendor ${vendor.vendorId}`}
                                        </h4>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                            {parseFloat(vendor.salesGrowth || '0') > 0 ? (
                                                <TrendingUp size={12} color="#10b981" />
                                            ) : parseFloat(vendor.salesGrowth || '0') < 0 ? (
                                                <TrendingDown size={12} color="#ef4444" />
                                            ) : (
                                                <span style={{ color: '#94a3b8' }}>—</span>
                                            )}
                                            <span style={{
                                                fontSize: '12px',
                                                color: parseFloat(vendor.salesGrowth || '0') > 0 ? '#10b981' :
                                                    parseFloat(vendor.salesGrowth || '0') < 0 ? '#ef4444' : '#64748b',
                                                fontWeight: '600'
                                            }}>
                                                {vendor.salesGrowth || '0%'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ textAlign: 'right' }}>
                                    <div style={{
                                        fontSize: '18px',
                                        fontWeight: '800',
                                        color: '#1e293b',
                                        marginBottom: '4px'
                                    }}>
                                        {formatCurrency(revenue)}
                                    </div>
                                    <div style={{
                                        padding: '4px 12px',
                                        background: isTop ? '#ede9fe' : '#f1f5f9',
                                        borderRadius: '20px',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        color: isTop ? '#7c3aed' : '#475569'
                                    }}>
                                        {percentage.toFixed(2)}%
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Payment Methods Chart - Based on API data
    const PaymentMethodsChart = () => {
        const currentStats = apiStats || stats;

        if (!currentStats?.paymentMethods?.length) {
            return (
                <div style={{
                    background: 'white',
                    borderRadius: '24px',
                    padding: isMobile ? '20px' : '28px',
                    marginBottom: isMobile ? '20px' : '28px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.05)',
                    textAlign: 'center'
                }}>
                    <h3 style={{ fontWeight: 800, marginBottom: 20 }}>
                        Payment Methods
                    </h3>
                    <p style={{ color: '#64748b' }}>No payment data available</p>
                </div>
            );
        }

        const paymentData = currentStats.paymentMethods.map(method => ({
            name: method.method.replace('_', ' '),
            value: parsePercentage(method.percentage),
            count: method.count || 0,
            color: method.method === 'Cash' ? '#10b981' :
                method.method === 'Online_Payment' ? '#3b82f6' :
                    '#8b5cf6'
        }));

        const totalTransactions = paymentData.reduce((sum, method) => sum + method.count, 0);

        return (
            <div style={{
                background: 'white',
                borderRadius: '24px',
                padding: isMobile ? '20px' : '28px',
                marginBottom: isMobile ? '20px' : '28px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 12px 32px rgba(0,0,0,0.05)',
                position: 'relative'
            }}>
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(135deg, #10b981, #3b82f6)'
                }} />

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                    }}>
                        <CreditCard size={24} />
                    </div>
                    <div>
                        <h3 style={{
                            fontSize: isMobile ? '18px' : '22px',
                            fontWeight: '800',
                            color: '#1e293b'
                        }}>
                            Payment Methods
                        </h3>
                        <p style={{ color: '#64748b' }}>
                            Transaction distribution by payment type
                        </p>
                    </div>
                </div>

                <div style={{ height: isMobile ? '250px' : '300px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={paymentData}
                                cx="50%"
                                cy="50%"
                                innerRadius={isMobile ? 60 : 80}
                                outerRadius={isMobile ? 100 : 120}
                                paddingAngle={2}
                                dataKey="value"
                                nameKey="name"
                                label={(entry) => `${entry.name}: ${entry.value.toFixed(2)}%`}
                            >
                                {paymentData.map((entry, index) => (
                                    <Cell key={index} fill={entry.color} stroke="white" strokeWidth={2} />
                                ))}
                            </Pie>
                            <Tooltip
                                formatter={(value, name) => [`${value.toFixed(2)}%`, name]}
                            />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    };

    /* ---------------- ERROR DISPLAY ---------------- */
    if (error && !stats) {
        return (
            <div style={{
                padding: "20px",
                textAlign: "center",
                color: "#ef4444",
                background: "#fef2f2",
                borderRadius: "12px",
                margin: "10px",
                border: "1px solid #fecaca"
            }}>
                <h4 style={{ fontWeight: 600 }}>⚠️ API Connection Error</h4>
                <p>{error}</p>
                <p style={{ fontSize: "12px", color: "#64748b", marginTop: "10px" }}>
                    Please check your internet connection and authentication.
                </p>
            </div>
        );
    }

    return (
        <>
            <SummaryCards />
            <DateTrendChart />
            <TopVendorsPerformance />
            <PaymentMethodsChart />
            <OrderTypeChart />
        </>
    );
};

export default Cateringrevenue;